'use client';

import type {
  CartWidget,
  ChargesWidget,
  Money,
  PhysicalAddress,
  ProductWidget,
  ServiceAddressWidget,
  WarningWidget,
} from '@/lib/TypesOpenApi';
import type {DeepReadonly} from '@/lib/typings/readonly';
import {persist} from '@/lib/vatio-persistence';
import {proxy, useSnapshot} from 'valtio';
import {isProductsEqual} from '../../routing/mapState2Api/compareUserSelections';
import {type BatteryBox, battery, resetBattery} from './battery';
import {
  type DeliveryOption,
  deliveryOption,
  resetDeliveryOption,
} from './deliveryOption';
import {type InternetBox, internet, resetInternet} from './internet';
import {type PhoneBox, phone, resetPhone} from './phone';
import {type PointsState, points, resetPoints} from './points';
import {type StaticIpBox, resetStaticIp, staticIp} from './staticIp';
import {
  type StreamingServicesBox,
  resetStreaming,
  streaming,
} from './streaming';
import {
  ShippingAddressState,
  resetShippingAddressState,
  shippingAddress,
} from './shippingAddress';
import {isNumber, sum} from './calc';

export * from './ProductBox';
export * from './internet';
export * from './phone';
export * from './battery';
export * from './points';
export * from './staticIp';
export * from './streaming';
export * from './deliveryOption';
export * from './shippingAddress';

export interface SelectedProduct {
  name: string;
  price: number | string | undefined; // here we expect either existing price` or 'included' string
  postfix?: string;
  productRefId?: string;
  productWidget?: DeepReadonly<ProductWidget>;
}

export interface PureCartState {
  address: PhysicalAddress;
  shippingAddress: ShippingAddressState;
  internet: InternetBox;
  phone: PhoneBox;
  battery: BatteryBox;
  streaming: StreamingServicesBox;
  deliveryOption: DeliveryOption;
  points: PointsState;
  staticIp: StaticIpBox;
  /**
   * Additional items that were added by back-end through flow
   * and not directly choosed by user
   * */
  additionalItems: Array<ProductWidget>;
}

export interface CartState extends PureCartState {
  /** Returns array of selected products with monthly charges */
  monthlyCharges: Array<SelectedProduct>;
  totalMonthly: number;

  /** Returns array of selected products with one-time charges */
  oneTimeCharges: Array<SelectedProduct>;
  totalOneTime: number;

  isMobileCartOpen: boolean;
}

export interface CartWidgetStore {
  widget?: CartWidget;
  monthlyChargesWidget?: ChargesWidget;
  oneTimeChargesWidget?: ChargesWidget;
  serviceAddressWidget?: ServiceAddressWidget;
  warnings?: DeepReadonly<Array<WarningWidget>>;
}

export const cartWidget = proxy<CartWidgetStore>({
  widget: undefined,
  get monthlyChargesWidget() {
    return this.widget?.chargesWidgets.find(
      (w: ChargesWidget) => w.widgetType === 'WIDGET_TYPE_MONTHLY',
    );
  },
  get oneTimeChargesWidget() {
    return this.widget?.chargesWidgets.find(
      (w: ChargesWidget) => w.widgetType === 'WIDGET_TYPE_ONE_TIME',
    );
  },
  get serviceAddressWidget() {
    return this.widget?.serviceAddressWidget;
  },
  get warnings() {
    return [
      this.oneTimeChargesWidget?.warningWidget,
      this.monthlyChargesWidget?.warningWidget,
    ].filter(Boolean);
  },
});

// allow wait till receive page widget data
export const useCartWidget = () => useSnapshot(cartWidget);

const address: PhysicalAddress = {
  city: '',
  state: '',
  streetAddress: '',
  unitNumber: '',
  zipcode: '',
};

// Possible to preload initial state from back-end
// TODO: check if we have such requirements
// if add any new field, add the new field to the below reset function also
export const store = persist<CartState>('cart', {
  address,
  shippingAddress,
  internet,
  phone,
  battery,
  streaming,
  deliveryOption,
  points,
  staticIp,

  additionalItems: [],

  get monthlyCharges() {
    return [...monthlyCharges(this)];
  },

  get totalMonthly() {
    return totalPrice(this.monthlyCharges);
  },

  get oneTimeCharges() {
    return [...oneTimeCharges(this)];
  },

  get totalOneTime() {
    return totalPrice(this.oneTimeCharges);
  },

  isMobileCartOpen: false,
});

export const resetAdditionalItems = () => {
  store.additionalItems = [];
};

export const resetCartState = () => {
  store.address = address;
  resetInternet();
  resetPhone();
  resetBattery();
  resetStreaming();
  resetDeliveryOption();
  resetPoints();
  resetStaticIp();
  resetShippingAddressState();
  resetAdditionalItems();

  store.isMobileCartOpen = false;
};

export const totalPrice = (charges: Array<SelectedProduct>): number =>
  charges
    .map(({price}: SelectedProduct) => price)
    .filter(isNumber)
    .reduce(sum, 0);

export function* monthlyCharges({
  internet,
  phone,
  additionalItems,
  staticIp,
  points,
}: PureCartState) {
  if (internet.productId) {
    yield {
      name: internet.name,
      price: internet.productWidget?.productCost?.units,
      postfix: internet.productWidget?.productCost?.unitText,
      productRefId: internet.productId,
      productWidget: internet.productWidget,
    };
  }

  if (internet.internetAddons) {
    yield* internet.internetAddons
      .filter(addon => addon.selected === true)
      .map(addon => ({
        name: addon?.productWidget?.productName || addon?.productId,
        price: addon?.productWidget?.productCost?.units,
        postfix: addon?.productWidget?.productCost?.unitText,
        productRefId: addon?.productWidget?.productId || addon?.productId,
      }));
  }

  if (phone.isActualProduct) {
    yield {
      name: phone.name,
      price: phone.price,
      postfix: phone._product?.productCost?.unitText,
      productRefId: phone._product?.productId,
    };
  }

  if (staticIp.productId && staticIp.price !== 0) {
    yield {
      name: staticIp.name,
      price: staticIp.price,
      postfix: staticIp._product?.productCost?.unitText,
      productRefId: staticIp._product?.productId,
    };
  }

  yield* mapPointsToSelectedProducts(
    points.included.filter(({chargeType}) => chargeType === 'MONTHLY'),
    points.additional.filter(({chargeType}) => chargeType === 'MONTHLY'),
  );

  // streaming excluded intentionally
  yield* additionalItems
    .filter(({chargeType}) => chargeType === 'MONTHLY')
    .map(product => ({
      name: product.productName || product.productId!,
      price: convertCostToPrice(product.productCost, product.quantity),
      postfix: product.productCost?.unitText,
      productRefId: product?.productId,
    }));
}

export function* oneTimeCharges({
  points,
  battery,
  additionalItems,
}: PureCartState) {
  yield* mapPointsToSelectedProducts(
    points.included.filter(({chargeType}) => chargeType === 'ONE_TIME'),
    points.additional.filter(({chargeType}) => chargeType === 'ONE_TIME'),
  );

  if (battery.isActualProduct) {
    yield {
      name: battery.name,
      price: battery.price,
    };
  }

  // streaming excluded intentionally

  yield* additionalItems
    .filter(({chargeType}) => chargeType === 'ONE_TIME')
    .map(product => ({
      name: product.productName || product.productId!,
      price: convertCostToPrice(product.productCost, product.quantity),
    }));
}

export const useCartState = () => useSnapshot(store);

export const toggleIsMobileCartOpen = () => {
  store.isMobileCartOpen = !store.isMobileCartOpen;
};

export const setServiceAddress = (address: PhysicalAddress | undefined) => {
  if (address) {
    store.address = address;
  }
};

/** Add item as additional, will replace existing if already there */
export const setItemToCart = (product: ProductWidget) => {
  const foundIndex = store.additionalItems.findIndex(a =>
    isProductsEqual(a, product),
  );
  if (foundIndex > -1) {
    // already exists, will replace
    store.additionalItems[foundIndex] = product;
    return;
  }

  // something new, will add if allowed
  store.additionalItems.push(product);
};

/**
 * Will remove item only from additional items section
 * TODO: remove from other sections also
 * */
export const removeItemFromCart = (product: ProductWidget) => {
  const foundIndex = store.additionalItems.findIndex(a =>
    isProductsEqual(a, product),
  );

  if (foundIndex === -1) {
    console.warn('Unkown item, cannot remove from cart', product);
    return;
  }

  // delete item
  store.additionalItems.splice(foundIndex, 1);
};

const mapPointsToSelectedProducts = (
  included: Array<ProductWidget>,
  additional: Array<ProductWidget>,
) => {
  return [
    ...included.map(p => ({
      name: `${p.quantity} ${p.productName}`,
      price: convertCostToPrice(p?.productCost, p?.quantity, {
        forceIncluded: true, // TODO: remove force after BE change prices for included products to correct ones
      }),
      postfix: p.productCost?.unitText,
    })),
    ...additional
      .filter(p => (p?.quantity || 0) > 0)
      .map(p => ({
        name: `${p.quantity} ${p.productName}`,
        price: convertCostToPrice(p?.productCost, p?.quantity),
        postfix: p.productCost?.unitText,
      })),
  ];
};

export interface ConvertCostOptions {
  forceIncluded?: boolean;
}
/** Will try to convert cost to price with fallback to included */
export const convertCostToPrice = (
  cost: Money = {},
  quantity = 0,
  {forceIncluded = false}: ConvertCostOptions = {},
): number | string | undefined => {
  const units = cost.units || 0;
  if (forceIncluded) {
    // consider as included
    return cost.unitText || 'included';
  }
  if (units <= 0) {
    return cost.unitText || 0;
  }
  // if quanity 0 or not provided, we not will fallback to included,
  // as it can signal that product should be removed
  return units * quantity;
};
